import isEqual from "lodash/isEqual";

/**
 * Normalizes a form value for dirty-state comparison.
 *
 * Treats empty strings, `null`, and `undefined` as equivalent, because
 * clearing an input in the UI produces `""` while the initial value is often
 * `undefined`. Without this normalization, the form stays "dirty" after the
 * user simply clears a field.
 */
const normalizeFormValue = (value: any): any => {
	if (value === "" || value === null) return undefined;
	if (Array.isArray(value)) return value.map(normalizeFormValue);
	if (value instanceof Date) return value.toISOString();
	if (value && typeof value === "object") {
		const result: Record<string, any> = {};
		for (const key of Object.keys(value)) {
			const normalized = normalizeFormValue(value[key]);
			// Strip keys whose normalized value is undefined so that
			// `{ field: undefined }` and `{}` compare as equal. This prevents
			// JSON-identical values from being reported as dirty just because
			// one side has an explicit undefined property.
			if (normalized !== undefined) {
				result[key] = normalized;
			}
		}
		return result;
	}
	return value;
};

/**
 * Compares current form data with initial form data using a normalized view.
 *
 * @returns `true` if the form has meaningful changes, `false` otherwise.
 */
export const isFormDataDirty = (
	formData: any,
	initialFormData: any
): boolean => {
	const current = normalizeFormValue(formData) ?? {};
	const initial = normalizeFormValue(initialFormData) ?? {};

	// Only compare keys present in the current form data. The fetched record
	// (initialFormData in edit mode) contains server-side metadata such as
	// _id, status, created_at, etc. that are not form fields and therefore
	// should not affect the dirty state.
	for (const key of Object.keys(current)) {
		if (!isEqual(current[key], initial[key])) {
			return true;
		}
	}
	return false;
};

/**
 * Returns a shallow diff of normalized values for debugging.
 * Only includes keys present in either object.
 */
export const getFormDataDiff = (
	formData: any,
	initialFormData: any
): Record<string, { current: any; initial: any }> => {
	const current = normalizeFormValue(formData) ?? {};
	const initial = normalizeFormValue(initialFormData) ?? {};
	const keys = new Set([...Object.keys(current), ...Object.keys(initial)]);
	const diff: Record<string, { current: any; initial: any }> = {};
	for (const key of keys) {
		if (!isEqual(current[key], initial[key])) {
			diff[key] = { current: current[key], initial: initial[key] };
		}
	}
	return diff;
};
